<template>
	<div
		v-if="sessionStore.activePriceBook"
		class="h-full overflow-y-auto px-10 pt-14"
		data-area="print"
	>
		<div class="flex items-center py-4 print:pt-0">
			<h1
				v-if="!hidePrintTitle"
				data-test-id="print-title"
				class="text-primary-700 hidden font-serif text-2xl font-semibold print:block"
			>
				{{ t('paymentOptionsSlide.print.costEstimateReview') }}
			</h1>
			<button
				@click="print()"
				class="text-primary-500 flex items-center gap-2 print:hidden"
			>
				<PrinterIcon class="text-primary-500 h-6 w-6" />
				{{ t('memorialGuide.print') }}
			</button>
			<PrintDate class="ml-auto" />
		</div>
		<div
			:class="{
				grid: true,
				'grid-cols-1': plans.length === 1,
				'grid-cols-2': plans.length === 2,
				'grid-cols-3': plans.length > 2,
			}"
		>
			<ReviewSlideCard
				v-for="(plan, index) in plans"
				:key="plan.id"
				:plan="plan"
				:index="index"
				:isSinglePlanSession="plans.length === 1"
				:otherLine2Conflict="otherLine2Conflict"
				@openCustomItemModal="openCustomItemModal(plan)"
			/>
		</div>

		<CustomItemModal
			:open="customItemModal"
			:plan="customItemModalPlan"
			:package-included="customItemModalPackageIncluded"
			@close="customItemModal = false"
			@submit="addCustomItem"
		/>
	</div>
</template>

<script setup lang="ts">
import ReviewSlideCard from './ReviewSlideCard.vue'
import type Plan from '@/classes/Plan'
import PrintDate from '@/components/common/PrintDate.vue'
import CustomItemModal from '@/components/documents/CustomItems/CustomItemModal.vue'
import { PbProductType } from '@/enums/PbProductType'
import { PbSubcategory } from '@/enums/PbSubcategory'
import { usePresentationLocale } from '@/i18n'
import type { ReviewSlideStoryblok } from '@/sbTypes'
import { usePlanStore } from '@/stores/plan'
import { usePresentationController } from '@/stores/presentationController'
import { useSessionStore } from '@/stores/session'
import type { CustomCartItem } from '@/types'
import { PrinterIcon } from '@heroicons/vue/24/solid'
import { Swiper } from 'swiper'
import { computed, onBeforeUnmount, onMounted, type PropType, ref } from 'vue'

const { t } = usePresentationLocale()
defineProps({
	blok: Object as PropType<ReviewSlideStoryblok>,
	parentSwiper: Swiper,
	active: Boolean,
	// The combined print page shows its own heading above this slide
	hidePrintTitle: Boolean,
})

const sessionStore = useSessionStore()
const planStore = usePlanStore()
const customItemModal = ref<boolean>(false)
const customItemModalPlan = ref<Plan>()
const customItemModalPackageIncluded = ref<boolean>(false)

const plans = computed(() => {
	return sessionStore.activePlans
})

const otherLine2Conflict = computed(() => {
	return plans.value.some((plan) => {
		const hasPackage = planStore.planIncludesPackage(plan.id)

		if (hasPackage) {
			return plan.customCartItems.some((item) => {
				return (
					item.category === PbProductType.Transportation &&
					item.subcategory === PbSubcategory.Other2 &&
					item.isSoftDeleted === false
				)
			})
		}

		return false
	})
})

const openCustomItemModal = (plan: Plan) => {
	customItemModal.value = true
	customItemModalPlan.value = plan
	customItemModalPackageIncluded.value = planStore.planIncludesPackage(plan.id)
}

const addCustomItem = (item: Omit<CustomCartItem, 'id' | 'isSoftDeleted'>) => {
	customItemModal.value = false
	customItemModalPlan.value?.addCustomItem(item)
}

// Update the swiper before printing and remove the event listener when the component is unmounted
onMounted(() => {
	const handleBeforePrint = async () => {
		usePresentationController().swiper?.update()
	}
	window.addEventListener('beforeprint', handleBeforePrint)

	onBeforeUnmount(() => {
		window.removeEventListener('beforeprint', handleBeforePrint)
	})
})

const print = () => window.print()
</script>
